import React from 'react';
import {
	InstallationCalculationPayload,
	InstallationCalculationType,
	TimkenBearingMaterial,
	TimkenUnit,
} from '../../../components/bearing-search/types/types';
import { handleActionError } from '../../utils/engineering-tools';
import {
	convertNumberInput,
	isNonZero,
	isValidBoundary,
	isValidRange,
} from '../../utils/validations';
import {
	FRICTION_COEFFICIENT_MAX,
	FRICTION_COEFFICIENT_MIN,
	INSTALLATION_FIT_MAX,
	INSTALLATION_FIT_MIN,
	POISSON_RATIO_MAX,
	POISSON_RATIO_MIN,
	YOUNGS_MODULUS_MIN,
	buildArrayData,
} from '../../../components/bearing-search/api/api';
import { text } from '../../data';

type InstallationFormValidationMap = Map<
	keyof InstallationFormState,
	{ invalid: boolean; message: string; }
>;

export type InstallationFormState = {
	unit: TimkenUnit;
	type: InstallationCalculationType;
	bearingType: string;
	bearingIs: number;
	partNumber: string;
	cupPartNumber: string;
	bearingID: Record<TimkenUnit, number>;
	bearingOD: Record<TimkenUnit, number>;
	minFit?: number;
	maxFit?: number;
	axialLoad?: number;
	shaftInnerDiameter?: number;
	housingOuterDiameter?: number;
	frictionCoefficient?: number;
	material: string;
	youngsModulus?: number;
	poissonRatio?: number;
	requestData?: Partial<InstallationCalculationPayload>;
	validation: InstallationFormValidationMap;
};

const getInstallationFormData = (
	state: InstallationFormState,
): [
		InstallationFormValidationMap,
		Partial<InstallationCalculationPayload> | undefined
	] => {
	const {
		type,
		unit,
		bearingType,
		bearingIs,
		partNumber,
		cupPartNumber,
		bearingID,
		bearingOD,
		minFit,
		maxFit,
		axialLoad,
		shaftInnerDiameter,
		housingOuterDiameter,
		frictionCoefficient,
		material,
		youngsModulus,
		poissonRatio,
	} = state;

	const validation = new Map( state.validation );
	const invalidBoundary = ! isValidBoundary( minFit, maxFit );
	const errorRangeText = `${ text.errorRange } [${ INSTALLATION_FIT_MIN }, ${ INSTALLATION_FIT_MAX }]`;
	let invalidInput = ! isValidRange(
		minFit,
		INSTALLATION_FIT_MIN,
		INSTALLATION_FIT_MAX,
	);

	validation.set( 'minFit', {
		invalid: invalidInput || invalidBoundary,
		message: invalidInput ? errorRangeText : text.errorBoundaryLowerTightLoose,
	} );

	invalidInput = ! isValidRange(
		maxFit,
		INSTALLATION_FIT_MIN,
		INSTALLATION_FIT_MAX,
	);

	validation.set( 'maxFit', {
		invalid: invalidInput || invalidBoundary,
		message: invalidInput ? errorRangeText : text.errorBoundaryUpperLooseTight,
	} );

	if ( validation.has( 'axialLoad' ) ) {
		validation.set( 'axialLoad', {
			invalid: ! isNonZero( axialLoad ),
			message: text.errorNonZero,
		} );
	}

	if ( validation.has( 'shaftInnerDiameter' ) ) {
		validation.set( 'shaftInnerDiameter', {
			invalid: ! isValidRange(
				shaftInnerDiameter,
				0,
				bearingID[ unit ],
				false,
				true,
			),
			message: text.shaftIdErrorLessThanBearingBore + bearingID[ unit ],
		} );
	}

	if ( validation.has( 'housingOuterDiameter' ) ) {
		validation.set( 'housingOuterDiameter', {
			invalid: ! isValidRange(
				housingOuterDiameter,
				bearingOD[ unit ],
				Infinity,
				true,
				false,
			),
			message: text.housingOdErrorGreaterThanBearingOd + bearingOD[ unit ],
		} );
	}

	validation.set( 'frictionCoefficient', {
		invalid: ! isValidRange(
			frictionCoefficient,
			FRICTION_COEFFICIENT_MIN,
			FRICTION_COEFFICIENT_MAX,
			true,
			true,
		),
		message: `${ text.errorRange } (${ FRICTION_COEFFICIENT_MIN }, ${ FRICTION_COEFFICIENT_MAX })`,
	} );

	validation.set( 'material', {
		invalid: ! material,
		message: text.errorRequired,
	} );

	if ( material === 'Custom' ) {
		validation.set( 'youngsModulus', {
			invalid: ! isValidRange( youngsModulus, YOUNGS_MODULUS_MIN, Infinity, true ),
			message: `${ text.errorGreaterThan } ${ YOUNGS_MODULUS_MIN }`,
		} );

		validation.set( 'poissonRatio', {
			invalid: ! isValidRange(
				poissonRatio,
				POISSON_RATIO_MIN,
				POISSON_RATIO_MAX,
				true,
			),
			message: `${ text.errorRange } (${ POISSON_RATIO_MIN }, ${ POISSON_RATIO_MAX })`,
		} );
	} else {
		validation.set( 'youngsModulus', {
			invalid: false,
			message: '',
		} );

		validation.set( 'poissonRatio', {
			invalid: false,
			message: '',
		} );
	}

	const isFormValid = ! Array.from( validation.values() ).some(
		( field ) => field.invalid,
	);
	let requestData: Partial<InstallationCalculationPayload> | undefined;

	if ( isFormValid ) {
		requestData = {
			type,
			unit,
			bearingType,
			bearingIs,
			partNumber,
			cupPartNumber,
			material,
			youngsModulus,
			poissonRatio,
			frictionCoefficient: buildArrayData( frictionCoefficient ),
		};

		const fit = [ minFit, maxFit ] as [number, number];

		if ( type === 'Inner' ) {
			requestData.innerFit = fit;
			requestData.shaftInnerDiameter = shaftInnerDiameter;
		} else {
			requestData.outerFit = fit;
			requestData.housingOuterDiameter = housingOuterDiameter;
		}

		if ( validation.has( 'axialLoad' ) ) {
			requestData.axialLoad = buildArrayData( axialLoad );
		}
	}

	return [ validation, requestData ];
};

const validationFields: Array<keyof InstallationFormState> = [
	'minFit',
	'maxFit',
	'axialLoad',
	'shaftInnerDiameter',
	'housingOuterDiameter',
	'frictionCoefficient',
	'material',
	'youngsModulus',
	'poissonRatio',
];

const initialState: InstallationFormState = {
	unit: 'M',
	type: 'Inner',
	bearingType: '',
	partNumber: '',
	cupPartNumber: '',
	material: '',
	bearingIs: 0,
	bearingID: {
		M: Infinity,
		I: Infinity,
	},
	bearingOD: {
		M: 0,
		I: 0,
	},
	validation: new Map(
		validationFields.map( ( key ) => [
			key,
			{
				invalid: true,
				message: text.errorRequired,
			},
		] ),
	),
};

const [ initialValidation, initialRequestData ] = getInstallationFormData( initialState );

export const initialInstallationFormState: InstallationFormState = {
	...initialState,
	validation: initialValidation,
	requestData: initialRequestData,
};

export type InstallationFormAction =
	| { type: 'change_unit'; payload: TimkenUnit; }
	| { type: 'change_min_fit'; payload: unknown; }
	| { type: 'change_max_fit'; payload: unknown; }
	| { type: 'change_axial_load'; payload: unknown; }
	| { type: 'change_shaft_inner_diameter'; payload: unknown; }
	| { type: 'change_housing_outer_diameter'; payload: unknown; }
	| { type: 'change_friction_coefficient'; payload: unknown; }
	| { type: 'change_youngs_modulus'; payload: unknown; }
	| { type: 'change_poissons_ratio'; payload: unknown; }
	| { type: 'change_material'; payload: TimkenBearingMaterial; }
	| { type: 'update_request_data'; }
	| { type: 'reset'; payload?: InstallationFormState; };

const installationFormReducer: React.Reducer<
	InstallationFormState,
	InstallationFormAction
> = ( state, action ) => {
	switch ( action.type ) {
		case 'change_unit': {
			return {
				...state,
				unit: action.payload,
			};
		}
		case 'change_min_fit': {
			return {
				...state,
				minFit: convertNumberInput( action.payload ),
			};
		}
		case 'change_max_fit': {
			return {
				...state,
				maxFit: convertNumberInput( action.payload ),
			};
		}
		case 'change_axial_load': {
			return {
				...state,
				axialLoad: convertNumberInput( action.payload ),
			};
		}
		case 'change_shaft_inner_diameter': {
			return {
				...state,
				shaftInnerDiameter: convertNumberInput( action.payload ),
			};
		}
		case 'change_housing_outer_diameter': {
			return {
				...state,
				housingOuterDiameter: convertNumberInput( action.payload ),
			};
		}
		case 'change_friction_coefficient': {
			return {
				...state,
				frictionCoefficient: convertNumberInput( action.payload ),
			};
		}
		case 'change_youngs_modulus': {
			return {
				...state,
				youngsModulus: convertNumberInput( action.payload ),
			};
		}
		case 'change_poissons_ratio': {
			return {
				...state,
				poissonRatio: convertNumberInput( action.payload ),
			};
		}
		case 'change_material': {
			const { name, youngsModulus, youngsModulusMM, poissonRatio } =
				action.payload;

			const modulus = state.unit === 'M' ? youngsModulusMM : youngsModulus;

			const elasticity = {
				youngsModulus: name === 'Custom' ? undefined : modulus,
				poissonRatio: name === 'Custom' ? undefined : poissonRatio,
			};

			return {
				...state,
				material: name || '',
				...elasticity,
			};
		}
		case 'update_request_data': {
			const [ validation, requestData ] = getInstallationFormData( state );

			return {
				...state,
				validation,
				requestData,
			};
		}
		case 'reset': {
			const newState = action.payload
				? action.payload
				: initialInstallationFormState;

			return {
				...newState,
			};
		}
	}

	return handleActionError( action );
};

export default installationFormReducer;
